import { AfterViewInit,Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { FetchAllCountries, FetchAllStates } from '../../store/customers.actions';
import { ICountry, IState } from '../../models/customers.models';

declare var google: any;

@Component({
  selector: 'app-edit-address',
  templateUrl: './edit-address.component.html',
  styleUrls: ['./edit-address.component.scss'],
})
export class EditAddressComponent implements OnInit, AfterViewInit {
  form!: FormGroup;
  subscriptions: Subscription[] = [];
  countries$: Observable<ICountry[]> = this.store.select(
    (state) => state.customers.allCountries
  );
  countries: ICountry[] = [];
  states$: Observable<IState[]> = this.store.select(
    (state) => state.customers.allStates
  );
  states: IState[] = [];
  country!: ICountry;
  state!: IState;
  city!: string;
  zip!: string;
  billing_country!: ICountry;
  billing_state!: IState;
  billing_city!: string;
  billing_zip!: string;
  lang!: string
  constructor(
    public dialogRef: MatDialogRef<EditAddressComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly fb: FormBuilder,
    private store: Store<AppState>,
  ) {
    this.store.dispatch(FetchAllCountries());
    this.store.dispatch(FetchAllStates());
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      name: new FormControl(this.data?.name ?? '', Validators.required),
      apartment: new FormControl(
        this.data?.apartment ?? '',
        Validators.required
      ),
      billing_apartment: new FormControl(
        this.data?.billing_apartment ?? '',
        Validators.required
      ),
      is_bill_same: new FormControl(this.data?.is_bill_same ?? false),
    });
    const sub1 = this.states$.subscribe((states) => {
      this.states = states;
    })
    const sub2 = this.countries$.subscribe((countries) => {
      this.countries = countries;
    });
    this.country = this.data?.country ?? '';
    this.state = this.data?.state ?? '';
    this.city = this.data?.city ?? '';
    this.zip = this.data?.zip ?? '';
    this.billing_country = this.data?.billing_country ?? '';
    this.billing_state = this.data?.billing_state ?? '';
    this.billing_city = this.data?.billing_city ?? '';
    this.billing_zip = this.data?.billing_zip ?? '';
    this.lang = this.data?.lang ?? '';
    this.subscriptions.push(sub1, sub2)
  }

  ngAfterViewInit(): void {
    this.addressAutoComplete('apartment');
    this.addressAutoComplete('billing_apartment');
  }

  addressAutoComplete(elementId: string): void {
    const center = { lat: 50.064192, lng: -130.605469 };
    // Create a bounding box with sides ~10km away from the center point
    const defaultBounds = {
      north: center.lat + 0.1,
      south: center.lat - 0.1,
      east: center.lng + 0.1,
      west: center.lng - 0.1,
    };
    const input = document.getElementById(elementId) as HTMLInputElement;
    const options = {
      bounds: defaultBounds,
      strictBounds: false,
    };

    if (!input) {
      console.error(`Element with id '${elementId}' not found`);
      return;
    }

    const autocomplete = new google.maps.places.Autocomplete(input, options);
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();

      if (!place) {
        console.error('No place found');
        return;
      }
      const address = place.formatted_address;
      let country: string = '';
      let state = '';
      let city = '';
      let zipCode = '';
      let short_name = ''
      // Extract country and state from address_components
      place.address_components.forEach((component: { types: string | string[]; long_name: string; short_name: string}) => {
        if (component.types.includes('country')) {
          country = component.long_name; // or component.short_name for ISO code
        }
        if (component.types.includes('administrative_area_level_1')) {
          state = component.long_name; // or component.short_name for abbreviation
        }
        if (component.types.includes('administrative_area_level_2')) {
          city = component.long_name;
        }
        if(component.types.includes('country')) {
          short_name = component.short_name
        }
        if (component.types.includes('postal_code')) {
          zipCode = component.long_name;
        }
      });
      if (elementId === 'apartment') {
        this.lang = short_name === 'IT' ? 'it_IT' : short_name === 'FR' ? 'fr_FR' : short_name === 'US' ? 'en_US' : 'it_IT';
        const apartment = place.formatted_address;
        this.form
        .get('apartment')
        ?.patchValue(apartment);
        this.country = this.countries.find((countr: ICountry) => (countr.name) === country)!;
        this.state = this.states.find((stat: IState) => (stat.name) === state)!;
        this.city = city;
        this.zip = zipCode;
        if (this.form.get('is_bill_same')?.value === true) {
          this.form.get('billing_apartment')?.setValue(apartment);
          this.billing_country = this.countries.find((countr: ICountry) => (countr.name) === country)!;
          this.billing_state = this.states.find((stat: IState) => (stat.name) === state)!;
          this.billing_city = city;
          this.billing_zip = zipCode;
        }
      }
      if (elementId === 'billing_apartment' && this.form.get('is_bill_same')?.value === false) {
        const billing_apartment = place.formatted_address;
        this.form
        .get('billing_apartment')
        ?.patchValue(billing_apartment);
        this.billing_country = this.countries.find((countr: ICountry) => (countr.name) === country)!;
        this.billing_state = this.states.find((stat: IState) => (stat.name) === state)!;
        this.billing_city = city;
        this.billing_zip = zipCode;
      }
    });
  }

  toggleBillingAddress(): boolean | void {
    if (this.form.get('apartment')?.invalid) {
      this.form.get('apartment')?.markAsTouched();
      this.form.get('is_bill_same')?.setValue(false);
      return false;
    }
    const sameAsShipping = this.form.get('is_bill_same')?.value;
    if (sameAsShipping) {
      this.copyShippingToBilling();
    } else {
      this.clearBilling();
    }
  }

  copyShippingToBilling() {
    this.form.patchValue({
      billing_apartment: this.form.get('apartment')?.value,
    });
    this.billing_country = this.country;
    this.billing_state = this.state;
    this.billing_city = this.city;
    this.billing_zip = this.zip;
  }

  clearBilling() {
    this.form.patchValue({
      billing_apartment: '',
    });
  }

  save(): boolean | void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return false;
    }
    const payload = {
      ...this.form.value,
      country: this.country,
      state: this.state,
      city: this.city,
      zip: this.zip,
      billing_country: this.billing_country,
      billing_state: this.billing_state,
      billing_city: this.billing_city,
      billing_zip: this.billing_zip,
      lang: this.lang
    }
    this.dialogRef.close(payload);
  }

  closed() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
